import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Alert, Badge, Button, Card, Checkbox, EmptyState, Inline, Stack, Text } from '../components/atoms';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea, useIsAdmin } from '../components/admin/AdminArea';
import { AdminConfirmDialog, type AdminConfirmRequest } from '../components/admin/AdminConfirmDialog';
import { AdminTable } from '../components/admin/AdminTable';
import { formatDateTime } from '../components/admin/adminFormat';
import {
  fetchAdminOptInPrompts,
  fetchAdminOptInResponses,
  setAdminOptInPromptActive,
  type AdminOptInPrompt,
  type AdminOptInResponse,
} from '../services/optInPromptsService';
import './AdminOptInPromptsPage.css';

const PAGE_SIZE = 25;
const EXPORT_PAGE_SIZE = 200;
const PROMPTS_QUERY_KEY = ['admin', 'opt-in-prompts'] as const;

/** RFC 4180 field; a leading =, +, - or @ is prefixed with ' so spreadsheets never run it as a formula. */
function csvField(value: string): string {
  const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function downloadCsv(fileName: string, csv: string): void {
  // Byte order mark first, so spreadsheet apps read the file as UTF-8 (umlauts in names).
  const url = URL.createObjectURL(new Blob([String.fromCharCode(0xfeff), csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function PromptPanel({ prompt }: { prompt: AdminOptInPrompt }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [grantedOnly, setGrantedOnly] = useState(true);
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const responses = useQuery({
    queryKey: ['admin', 'opt-in-prompts', prompt.id, 'responses', grantedOnly, offset],
    queryFn: () => fetchAdminOptInResponses(prompt.id, { limit: PAGE_SIZE, offset, grantedOnly }),
    placeholderData: keepPreviousData,
  });
  const consentFields = prompt.fields.filter((f) => f.type === 'consent');
  const textFields = prompt.fields.filter((f) => f.type === 'text');
  const fieldLabel = (key: string): string => t(`optInPrompts.${prompt.id}.fields.${key}.short`);
  const title = t(`optInPrompts.${prompt.id}.title`);
  const items = responses.data?.items ?? [];
  const total = responses.data?.total ?? 0;
  const yesNo = (value: unknown): string => (value === true ? t('adminArea.optIns.yes') : t('adminArea.optIns.no'));

  const toggleActive = (): void => {
    const next = !prompt.active;
    setConfirm({
      title: next ? t('adminArea.optIns.activateTitle', { title }) : t('adminArea.optIns.deactivateTitle', { title }),
      message: next ? t('adminArea.optIns.activateMessage') : t('adminArea.optIns.deactivateMessage'),
      confirmLabel: next ? t('adminArea.optIns.activate') : t('adminArea.optIns.deactivate'),
      onConfirm: async () => {
        await setAdminOptInPromptActive(prompt.id, next);
        await queryClient.invalidateQueries({ queryKey: PROMPTS_QUERY_KEY });
      },
    });
  };

  async function exportCsv(): Promise<void> {
    setExporting(true);
    setExportFailed(false);
    try {
      const all: AdminOptInResponse[] = [];
      for (let from = 0; ; from += EXPORT_PAGE_SIZE) {
        const page = await fetchAdminOptInResponses(prompt.id, { limit: EXPORT_PAGE_SIZE, offset: from, grantedOnly: true });
        all.push(...page.items);
        if (page.items.length < EXPORT_PAGE_SIZE || all.length >= page.total) break;
      }
      const header = [
        t('adminArea.optIns.col.name'),
        t('adminArea.optIns.col.email'),
        ...textFields.map((f) => fieldLabel(f.key)),
        ...consentFields.map((f) => fieldLabel(f.key)),
        t('adminArea.optIns.col.date'),
      ];
      const rows = all.map((r) => [
        r.userName ?? '',
        r.userEmail ?? '',
        ...textFields.map((f) => String(r.answers[f.key] ?? '')),
        ...consentFields.map((f) => yesNo(r.answers[f.key])),
        r.createdAt,
      ]);
      const csv = [header, ...rows].map((row) => row.map(csvField).join(',')).join('\r\n');
      downloadCsv(`reefguide-${prompt.id}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    } catch {
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  }

  return (
    <Section
      title={title}
      actions={
        <Button variant={prompt.active ? 'secondary' : 'primary'} onClick={toggleActive}>
          {prompt.active ? t('adminArea.optIns.deactivate') : t('adminArea.optIns.activate')}
        </Button>
      }
    >
      <Stack gap="6">
        <Card padding="lg">
          <Stack gap="3">
            <Inline gap="2" align="center" wrap>
              <Badge tone={prompt.active ? 'success' : 'neutral'}>
                {prompt.active ? t('adminArea.optIns.statusActive') : t('adminArea.optIns.statusInactive')}
              </Badge>
              <Text as="span" size="sm" color="secondary">{t('adminArea.optIns.version', { version: prompt.version })}</Text>
            </Inline>
            <ul className="admin-opt-ins__stats">
              <li><Text as="span">{t('adminArea.optIns.granted', { count: prompt.granted })}</Text></li>
              <li><Text as="span">{t('adminArea.optIns.declined', { count: prompt.declined })}</Text></li>
              {consentFields.map((f) => (
                <li key={f.key}>
                  <Text as="span">{t('adminArea.optIns.perConsent', { label: fieldLabel(f.key), count: prompt.consents[f.key] ?? 0 })}</Text>
                </li>
              ))}
            </ul>
          </Stack>
        </Card>

        <Inline gap="4" align="center" justify="between" wrap>
          <Checkbox
            label={t('adminArea.optIns.grantedOnly')}
            checked={grantedOnly}
            onChange={(e) => {
              setGrantedOnly(e.target.checked);
              setOffset(0);
            }}
          />
          <Button variant="secondary" onClick={() => void exportCsv()} isLoading={exporting} disabled={prompt.granted === 0}>
            {t('adminArea.optIns.exportCsv')}
          </Button>
        </Inline>
        {exportFailed && <Alert tone="danger">{t('adminArea.optIns.exportError')}</Alert>}

        {responses.isLoading && (
          <LiveRegion>
            <Text>{t('adminArea.optIns.loading')}</Text>
          </LiveRegion>
        )}
        {responses.isError && (
          <LoadErrorState title={t('adminArea.optIns.loadError')} titleLevel={3} onRetry={() => void responses.refetch()} />
        )}
        {!responses.isLoading && !responses.isError && items.length === 0 && (
          <EmptyState
            icon={<Megaphone size={48} strokeWidth={1.5} aria-hidden="true" />}
            headline={t('adminArea.optIns.emptyTitle')}
            description={prompt.active ? t('adminArea.optIns.emptyMessageActive') : t('adminArea.optIns.emptyMessageInactive')}
            actionLabel={prompt.active ? undefined : t('adminArea.optIns.activate')}
            onAction={prompt.active ? undefined : toggleActive}
            headingLevel={3}
          />
        )}
        {!responses.isLoading && !responses.isError && items.length > 0 && (
          <Stack gap="4">
            <LiveRegion>
              <Text size="sm" color="muted">
                {t('adminArea.optIns.range', { from: offset + 1, to: Math.min(offset + PAGE_SIZE, total), total })}
              </Text>
            </LiveRegion>
            <AdminTable caption={t('adminArea.optIns.tableCaption', { title })}>
              <thead>
                <tr>
                  <th scope="col">{t('adminArea.optIns.col.user')}</th>
                  {textFields.map((f) => <th key={f.key} scope="col">{fieldLabel(f.key)}</th>)}
                  {consentFields.map((f) => <th key={f.key} scope="col">{fieldLabel(f.key)}</th>)}
                  <th scope="col">{t('adminArea.optIns.col.date')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.userId}>
                    <th scope="row">
                      {r.userName ?? r.userEmail ?? r.userId}
                      {r.userName && r.userEmail && (
                        <Text variant="caption" className="rg-admin-table__muted">{r.userEmail}</Text>
                      )}
                    </th>
                    {textFields.map((f) => (
                      <td key={f.key} className="admin-opt-ins__handle">
                        {typeof r.answers[f.key] === 'string' ? `@${String(r.answers[f.key])}` : '–'}
                      </td>
                    ))}
                    {consentFields.map((f) => <td key={f.key}>{yesNo(r.answers[f.key])}</td>)}
                    <td>{formatDateTime(r.createdAt, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </AdminTable>
            {total > PAGE_SIZE && (
              <Inline gap="2" justify="between" wrap>
                <Button variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                  {t('adminArea.optIns.previous')}
                </Button>
                <Button variant="secondary" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                  {t('adminArea.optIns.next')}
                </Button>
              </Inline>
            )}
          </Stack>
        )}
      </Stack>
      <AdminConfirmDialog request={confirm} onClose={() => setConfirm(null)} errorMessage={t('adminArea.optIns.toggleError')} />
    </Section>
  );
}

/**
 * Admin → Opt-ins: switch each opt-in prompt (e.g. the Instagram campaign) on or
 * off, see how many users agreed, list their current answers with the Instagram
 * name and export the consents as CSV (GET/PUT /api/v1/admin/opt-in-prompts).
 */
export function AdminOptInPromptsPage() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const prompts = useQuery({ queryKey: PROMPTS_QUERY_KEY, queryFn: fetchAdminOptInPrompts, enabled: isAdmin });

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.optIns.description')} />
          <AdminArea>
            {prompts.isLoading && (
              <LiveRegion>
                <Text>{t('adminArea.optIns.loading')}</Text>
              </LiveRegion>
            )}
            {prompts.isError && <LoadErrorState title={t('adminArea.optIns.loadError')} onRetry={() => void prompts.refetch()} />}
            {(prompts.data ?? []).map((prompt) => (
              <PromptPanel key={prompt.id} prompt={prompt} />
            ))}
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminOptInPromptsPage;
